import { useState } from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import Icon from "components/Icon";
import { APP_VERSION } from "config/version";
import { linksInstitucionais, canaisAtendimento, formularioContato } from "config/linksInstitucionais";
import { temSessao } from "services/sessao";
import LinkExterno from "components/LinkExterno";
import RedesSociais from "components/RedesSociais";
import AssinaturaMarca from "components/AssinaturaMarca";
import SeloGooglePlay from "components/SeloGooglePlay";
import { appAndroidPublicado } from "config/appAndroid";
import { ehAndroidNoNavegador } from "services/plataforma";

// Ícone de cada canal de atendimento. Fica aqui, e não no módulo de config, que é
// dado e não apresentação — o Quem somos desenha os mesmos canais com outro tamanho.
//
// `marca` e `icone` são coisas diferentes: o WhatsApp é um logotipo, e logotipo se
// reconhece pela silhueta cheia — desenhá-lo em contorno, junto com o resto da interface,
// é o que tiraria dele a função de ser reconhecido de relance. Os outros dois são ícones de
// sistema e seguem o Material Symbols.
//
// Todos na mesma cor (ICONE_TILE), e não na cor de cada marca: sobre o rodapé navy, o verde
// do WhatsApp ao lado do azul do Facebook, do rosa do Instagram e do vermelho do YouTube
// virava uma fileira de cinco cores disputando atenção, e a silhueta cheia já basta para
// reconhecer cada uma. O Quem somos, sobre fundo claro, continua com as cores de marca.
const ICONE_CANAL = {
  whatsapp: { marca: FaWhatsapp },
  email: { icone: "mail" },
  formulario: { icone: "support_agent" },
};

// Branco a 90% em repouso, cheio no hover do tile (o `group` está no TILE). Dá ≈8:1 sobre o
// tile, folgado para o 3:1 que um ícone precisa.
const ICONE_TILE = "text-white/90 transition group-hover:text-white";

// O tile da coluna "Atendimento", compartilhado pelo "Fale conosco" (com rótulo) e pelos
// tiles só de ícone (canais externos e redes). Quem usa acrescenta só a medida: padding +
// texto num caso, h-11 w-11 no outro. Numa constante porque o RedesSociais recebe a classe
// por prop, e o visual dos dois vizinhos tem que sair do mesmo lugar.
//
// Translúcido sobre o navy (branco a 10%, fio a 15%), e não o card branco de antes: no
// rodapé escuro o branco sólido pulava do fundo como etiqueta colada, e era o elemento
// mais forte da faixa sem ser o mais importante. Assim ele ainda lê como botão, mas do
// mesmo material do fundo. Escolhido entre três variantes comparadas em artefato (ver
// prompts/home-com-fotos.md).
const TILE =
  "group inline-flex items-center rounded-2xl bg-white/10 text-white ring-1 ring-white/15 transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-dark";

// Wordmark oficial da Asaas (public/images/asaas.svg, recolorido para a cor de
// marca #0038E5 — o arquivo do site deles vem branco, para header escuro). Com o
// rodapé em navy o azul some no fundo, então o <img> volta ao branco por filtro
// (brightness-0 invert) em vez de trocar o arquivo: o Quem somos e qualquer outro
// uso sobre fundo claro continuam com a cor de marca.
// O onError no <img> mantém o fallback pro ícone genérico caso o asset suma num
// deploy: selo quebrado num rodapé de confiança é pior que selo genérico.
const ASAAS_LOGO = "/images/asaas.svg";

// Selos de credibilidade. Cada afirmação precisa ser verdadeira e no escopo certo:
// o reCAPTCHA hoje só roda na tela de entrada (ver src/pages/Login.jsx), então
// o selo fala em "acesso protegido" — e não no site inteiro.
//
// A cor de cada ícone é o que carrega o selo (não há cápsula em volta), então cada
// uma tem um motivo: verde = convenção de "conexão segura", azul do Google = o
// reCAPTCHA é produto deles, rosa da marca = o pagamento é nosso fluxo.
const trustBadges = [
  { icone: "lock", label: "Conexão segura (SSL)", color: "text-success" },
  { icone: "verified_user", label: "Acesso protegido por reCAPTCHA", color: "text-[#4285F4]" },
  {
    icone: "credit_card",
    // Com o logotipo presente ele é quem nomeia a Asaas, então o texto para em
    // "por"; sem ele, o nome volta pro texto pra afirmação não ficar truncada.
    label: "Pagamentos processados por",
    labelSemLogo: "Pagamentos processados pela Asaas",
    color: "text-primary",
    logo: ASAAS_LOGO,
  },
];

// `destaque` é o véu rosa do "Quero trabalhar": o único ponto de cor do rodapé, porque é
// o convite que só aparece aqui — "Buscar profissional" tem o CTA do corpo da página.
const actionLinks = [
  { to: "/profissional", label: "Buscar profissional" },
  { to: "/usuario/novo", label: "Quero trabalhar", destaque: true },
];

// `resumidoNoMobile` no lugar de um `className` solto: quem monta o rodapé
// (src/App.jsx) não precisa saber quais classes escondem o quê, só dizer se
// aquela rota é uma tela interna. Fora da Home, abaixo de md, o rodapé mostra
// só a assinatura da marca — ver a nota da própria assinatura, mais abaixo.
//
// `noApp` é o rodapé da tela de Login do app, a única tela de lá que o monta (ver
// src/App.jsx). Ele sai sem três partes escritas para a web: o bloco de marca (o logo
// aponta para "/", que no app volta ao próprio Login; "Buscar profissional" exige
// sessão; "Quero trabalhar" repete o "Criar conta" logo acima), a faixa de selos e o
// "Excluir minha conta" (ver `soNaWeb` no config).
function Footer({ resumidoNoMobile = false, noApp = false }) {
  const currentYear = new Date().getFullYear();
  // Versão do build fica só como tooltip (title), não como texto visível: é
  // informação de debug/deploy útil para suporte/dev inspecionando a página,
  // não algo que o usuário final precisa ver no rodapé.
  const versionFront = APP_VERSION;

  // Vira true se o SVG da Asaas ainda não foi adicionado em public/images/ —
  // aí o selo cai de volta pro ícone genérico em vez de mostrar imagem quebrada.
  const [logoFalhou, setLogoFalhou] = useState(false);

  // O bloco de marca (logo + "Buscar profissional"/"Quero trabalhar") é aquisição:
  // fala com quem ainda não entrou. Para quem já tem sessão vira ruído — a logo já
  // está no NavBar e os dois CTAs levam a lugares que o menu logado cobre melhor.
  // Leitura direta do localStorage, sem state, igual ao NavBar (ver NavBar.jsx:148):
  // os dois remontam a cada troca de rota, então login/logout já refletem aqui.
  const logado = temSessao();
  // Sem o bloco de marca o rodapé é o mesmo de quem está logado: a grade de 2 colunas.
  const semBlocoDeMarca = logado || noApp;
  const links = noApp ? linksInstitucionais.filter((link) => !link.soNaWeb) : linksInstitucionais;
  // O selo do app Android aparece para todo visitante da web, em qualquer sistema e qualquer
  // largura, logado ou não (pedido do Francisco). A tarja da Home (pages/Home/TarjaApp.jsx) é que
  // fica só no Android. Dentro do app o selo não faz sentido. E enquanto o app estiver em teste
  // fechado o link não abre para quem é de fora (ver config/appAndroid.js).
  const mostrarSeloApp = appAndroidPublicado() && !noApp;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer id="footer">
      {/* Assinatura da marca: é tudo o que sobra do rodapé nas telas internas da
          web abaixo de md. Antes o rodapé inteiro sumia ali, e a página terminava no
          último card, sem fecho nenhum — o excesso de links no fim de uma tela de
          trabalho continua sendo demais, mas acabar em nada faz a página parecer
          cortada. O que ela é, e por que é texto solto e não seção, está no próprio
          componente. Quem carrega bg-dark/border-t é o container do conteúdo
          completo, logo abaixo.

          No app este rodapé só monta no Login (ver App.jsx), com o mesmo corte: no
          celular só a assinatura, em tablet o conteúdo completo. Nas outras telas a
          mesma assinatura fecha o menu sanduíche (NavBar.jsx). */}
      {/* No Login do app a assinatura é o fim da tela de verdade: o respiro de baixo soma a
          barra de navegação do Android (--safe-bottom), como o wrapper completo faz abaixo. */}
      {/* O selo do app vale em qualquer largura. Nas telas internas do celular sobra só a
          assinatura, e por isso ele vem junto dela, com a linha legal, em vez de ficar só na
          barra de baixo do rodapé completo (que ali está escondido). */}
      {resumidoNoMobile && mostrarSeloApp && (
        <div className="flex flex-col items-center gap-2 px-6 pt-8 text-center md:hidden">
          <SeloGooglePlay origem="rodape" className="h-12" novaAba={!ehAndroidNoNavegador()} />
          <p className="text-xs text-dark/35">
            Google Play e o logotipo do Google Play são marcas da Google LLC.
          </p>
        </div>
      )}
      {resumidoNoMobile && (
        <AssinaturaMarca
          className={`px-6 md:hidden ${mostrarSeloApp ? "pt-6" : "pt-8"} ${noApp ? "pb-[calc(2rem+var(--safe-bottom))]" : "pb-8"}`}
        />
      )}

      {/* border-t/bg-dark ficam neste wrapper, e não no <footer>: são as classes que
          fazem o conteúdo completo ler como seção — na assinatura acima, de
          propósito, não tem nem uma nem outra. Precisam de um elemento à parte, e
          não da própria div com max-w-7xl logo abaixo, porque o fio e o fundo têm
          que ir de ponta a ponta da tela; o max-w-7xl é só o miolo, com padding. */}
      {/* No app o rodapé é o fim da tela de verdade, então o fundo navy desce por baixo da
          barra de navegação do Android e o conteúdo para acima dela (--safe-bottom). */}
      <div className={`${resumidoNoMobile ? "hidden border-t border-white/10 bg-dark md:block" : "border-t border-white/10 bg-dark"}${noApp ? " pb-[var(--safe-bottom)]" : ""}`}>
        {/* O respiro vertical tem um degrau só: `md` corta, `xl` devolve. 80px de folga
            em volta de uma faixa de 20px lê como seção num monitor largo e como buraco
            num de 900px — daí escalar o espaçamento por breakpoint, e não a fonte. O
            valor base fica intocado de propósito: abaixo de md o rodapé completo só
            aparece na Home (ver src/App.jsx), e lá ele já está na medida. */}
        <div className="mx-auto max-w-7xl px-6 py-12 sm:px-8 md:py-8 lg:px-10 xl:py-12">
          {/* Sem o bloco de marca a grade cai para 2 colunas: manter as 3 deixaria
              uma coluna de 1.2fr vazia empurrando "Ajuda" e "Atendimento" pra direita.
              As 2 colunas entram já em md, e não em lg: em 768px cabem folgadas, e
              empilhar ali desperdiça metade da largura e dobra a altura do rodapé.

              Logado, essas 2 colunas são de max-content e o par vai centralizado, não
              duas metades de 1fr: o conteúdo delas mede ~165px e ~310px, então em
              metades de um container de 1280px sobravam ~275px vazios só do lado
              direito — a única seção do rodapé fora do eixo, já que a faixa de selos é
              centralizada e a linha legal vai de borda a borda. Deslogado nada disso se
              aplica: lá o terceiro bloco preenche a largura.

              O vão entre as duas cresce por degrau (96 → 160 → 224 → 288px) em vez de
              ficar num valor só: com as colunas justas, é ele que separa os dois blocos
              — e uma distância que basta em 768px lê como aperto num monitor largo, onde
              o par centralizado tem muito branco em volta. Escala até 2xl porque é onde
              a folga lateral é maior; acima disso um vão maior começaria a desmanchar o
              par em duas ilhas soltas. */}
          <div
            className={`grid gap-10 md:gap-8 xl:gap-10 ${
              semBlocoDeMarca
                ? "md:grid-cols-[max-content_max-content] md:justify-center md:gap-x-24 lg:gap-x-40 xl:gap-x-56 2xl:gap-x-72"
                : "md:grid-cols-2 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.8fr)]"
            }`}
          >
            {!semBlocoDeMarca && (
              // Em md são três blocos numa grade de 2 colunas: a marca toma a linha
              // inteira e "Ajuda"/"Atendimento" dividem a de baixo. Sem o col-span a
              // grade quebraria em L, com "Atendimento" sozinho numa terceira linha.
              <div className="max-w-xl md:col-span-2 lg:col-span-1">
                <Link to="/" className="inline-flex items-center gap-3">
                  <img
                    src="/images/logo-negativo.svg"
                    alt="Nova Faxina"
                    className="h-20 w-auto md:h-16 xl:h-20"
                  />
                </Link>

                {/* py-3 garante ~44px de altura de toque (mínimo recomendado
                    iOS/Material) — py-2 anterior ficava em ~36px */}
                <div className="mt-6 flex flex-wrap gap-3">
                  {actionLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`btn text-white ring-1 focus:ring-2 focus:ring-primary focus:ring-offset-dark ${
                        link.destaque
                          ? "bg-primary/20 ring-primary/60 hover:bg-primary/35"
                          : "ring-white/30 hover:bg-white/10"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>

                {/* Texto, e não um terceiro botão: é o caminho do Google até as páginas por
                    cidade (sem link nenhum, ele não as indexava) e de quem quer ver se a cidade
                    é atendida. Não disputa com os dois convites acima. */}
                <Link
                  to="/diaristas"
                  className="mt-4 inline-flex min-h-[44px] items-center text-sm text-white/80 underline-offset-2 transition hover:text-white hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-dark"
                >
                  Diaristas por cidade
                </Link>
              </div>
            )}

            {/* Divisor sutil entre blocos empilhados em mobile — some a partir de md,
                onde a grid já separa os blocos espacialmente. O breakpoint acompanha o
                da grade acima: mantido em lg, sobraria um fio cortando metade da largura
                e 32px de padding no topo de colunas que já estão lado a lado. Logado,
                este vira o primeiro bloco do rodapé e não pode ter borda: não há nada
                acima dele. */}
            <div className={`${semBlocoDeMarca ? "" : "border-t border-white/10 pt-8"} md:border-t-0 md:pt-0`}>
              {/* Título claro, mas abaixo do branco dos links: uppercase + tracking largo em
                  14px já reduz muito o peso percebido, e no cinza do .t-sobre o rótulo
                  sumiria no navy */}
              <h2 className="t-sobre text-white/60">
                Ajuda e condições
              </h2>
              <nav className="mt-4 flex flex-col gap-3" aria-label="Links do rodapé">
                {links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="w-fit text-base text-white transition hover:text-accent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-dark"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="border-t border-white/10 pt-8 md:border-t-0 md:pt-0">
              <h2 className="t-sobre text-white/60">
                Atendimento
              </h2>

              {/* Tudo aqui divide o mesmo tile translúcido — o rodapé não é superfície de
                  conversão e um botão preenchido competiria com os CTAs do corpo da página.

                  O formulário é o único com rótulo: é interno, não tem logotipo que o
                  nomeie, e é o caminho que a pessoa que chegou ao rodapé sem achar resposta
                  vai querer ler. Vira <Link>, e não <a href="/quemsomos#contato">, que
                  recarregaria a aplicação inteira só para descer até o formulário. */}
              <div className="mt-4 flex flex-col gap-3">
                <Link
                  to={formularioContato.to}
                  className={`${TILE} w-full gap-3 px-4 py-3 text-sm sm:w-fit`}
                >
                  <Icon name={ICONE_CANAL.formulario.icone} className={`h-5 w-5 shrink-0 ${ICONE_TILE}`} />
                  {formularioContato.label}
                </Link>

                {/* Uma fileira de tiles só de ícone com os canais externos (WhatsApp,
                    e-mail). Cada um é um logotipo ou um ícone que se reconhece sozinho; o
                    número e o endereço, que antes eram o rótulo, vão para o nome acessível e
                    para o title ("WhatsApp: (43) …"), que é o que o `nome` do config existe
                    para nomear. Quadrado de 44px para valer como área de toque na Home mobile. */}
                <ul className="flex items-center gap-3">
                  {canaisAtendimento.map((canal) => {
                    const { marca: Marca, icone } = ICONE_CANAL[canal.id];
                    const nome = `${canal.nome}: ${canal.label}`;
                    // LinkExterno: na web é o mesmo <a target="_blank">; no app abre no
                    // navegador embutido (ou entrega o wa.me ao WhatsApp). Sem ele, numa
                    // WebView o alvo _blank carregaria o destino por cima do bundle.
                    return (
                      <li key={canal.id}>
                        <LinkExterno
                          href={canal.href}
                          aria-label={nome}
                          title={nome}
                          className={`${TILE} h-11 w-11 justify-center`}
                        >
                          {Marca ? (
                            <Marca size={20} className={`shrink-0 ${ICONE_TILE}`} aria-hidden="true" />
                          ) : (
                            <Icon name={icone} className={`h-5 w-5 shrink-0 ${ICONE_TILE}`} />
                          )}
                        </LinkExterno>
                      </li>
                    );
                  })}
                </ul>

                {/* As redes vêm num bloco próprio, abaixo dos canais, e não na mesma
                    fileira: rede social não é canal de atendimento (ver a nota de
                    `redesSociais` no config), e seis tiles iguais lado a lado liam como
                    seis canais. O "Siga-nos em" é o que nomeia o grupo, empilhado sobre
                    os ícones — como um rótulo de coluna, e não ao lado deles, que os
                    lia como mais um tile da fileira. A caixa alta vem do .t-sobre, não
                    do texto: o leitor de tela lê a frase normal e o rótulo casa com os
                    títulos das colunas. */}
                <div className="flex flex-col gap-2">
                  <span className="t-sobre mt-4 text-white/60">Siga-nos em</span>
                  <RedesSociais classeDoLink={`${TILE} h-11 w-11 justify-center`} classeDoIcone={ICONE_TILE} />
                </div>
              </div>
            </div>
          </div>

          {/* Faixa de credibilidade: sem cápsula e sem divisor próprio — fica no
              respiro entre a grade e a linha legal, que já tem a borda. O peso vem
              só da cor do ícone, então a faixa precisa de folga generosa (py-10)
              pra se ler como uma seção e não como sobra da grade acima.

              Essa folga é a do desktop largo: eram 112px (mt-8 + py-10) em volta de uma
              linha de 20px, quase um quarto da altura do rodapé numa tela de 1000px.
              Abaixo de xl ela cai pela metade — ainda o dobro do respiro dos blocos
              vizinhos, que é o que sustenta a leitura de faixa própria. O gap-x menor
              é o que faz os três selos ainda caberem numa linha em 768px. */}
          {/* No app a faixa inteira sai, não só o reCAPTCHA (que lá nem roda): a tela de
              entrada do aplicativo instalado pela loja não é onde se conquista confiança com
              selo — e uma faixa com um selo só, ou dois, leria como sobra. */}
          {!noApp && (
          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-5 py-10 md:mt-6 md:gap-x-6 md:gap-y-4 md:py-6 xl:mt-8 xl:gap-x-10 xl:gap-y-5 xl:py-10">
            {trustBadges.map(({ icone, label, labelSemLogo, color, logo }) => {
              const usaLogo = logo && !logoFalhou;
              // Com logo a ordem inverte: o texto termina em "por" e o logotipo
              // fecha a frase, no lugar onde o nome da marca seria lido.
              return (
                <li key={label} className="flex items-center gap-2.5 text-sm text-white/70">
                  {usaLogo ? (
                    <>
                      {label}
                      {/* h-3.5 (14px): o wordmark é todo caixa-baixa, então casar a
                          altura com o font-size deixa ele um pouco maior que a
                          altura-x do texto — presença de marca sem desequilibrar */}
                      <img
                        src={logo}
                        alt="Asaas"
                        onError={() => setLogoFalhou(true)}
                        className="h-3.5 w-auto shrink-0 brightness-0 invert"
                      />
                    </>
                  ) : (
                    <>
                      <Icon name={icone} className={`h-4 w-4 shrink-0 ${color}`} />
                      {labelSemLogo || label}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          )}

          {/* Sem a faixa de selos, o mt-10 repõe o respiro que ela fazia entre a grade e a
              linha legal. */}
          <div className={`${noApp ? "mt-10 " : ""}flex flex-col gap-4 border-t border-white/10 pt-6 text-left text-sm text-white/70 md:flex-row md:items-start md:justify-between`}>
            <div className="space-y-1">
              <p>© {`${currentYear}`} Nova Faxina. Todos os direitos reservados.</p>
              {/* No celular a linha corrida quebrava onde a largura mandasse, partindo o CNPJ
                  ou o endereço ao meio. Abaixo de md cada parte vira uma linha (razão social,
                  CNPJ, endereço, cidade); de md para cima volta a ser uma linha só, com os
                  separadores, que só existem nela. */}
              <p>
                <span className="block md:inline">Nova Faxina Serviços Ltda.</span>
                <span className="hidden md:inline"> | </span>
                <span className="block md:inline">CNPJ: 69.272.690/0001-34</span>
              </p>
              {/* Atribuição que as diretrizes de marca do Google pedem onde o selo é usado. */}
              {mostrarSeloApp && (
                <p className="text-xs text-white/50">
                  Google Play e o logotipo do Google Play são marcas da Google LLC.
                </p>
              )}
              <span className="text-white/25 text-xs">{`${versionFront}`}</span>
            </div>
            {/* Nova aba só fora do Android: lá o link vai para o app da Play Store, e a aba
                ficaria para trás. No computador a loja abre ao lado e o site continua aberto. */}
            {mostrarSeloApp && (
              <div className="flex flex-col gap-2 md:items-center">
                <span className="text-xs font-semibold text-white/70">Baixe o app</span>
                <SeloGooglePlay origem="rodape" className="h-12" novaAba={!ehAndroidNoNavegador()} />
              </div>
            )}
            {/* self-end alinha à direita no empilhamento mobile; a partir de md o
                justify-between já joga o botão pra ponta e o self volta pro topo da
                linha. O -mr-3 anula o padding lateral pro texto ficar opticamente
                rente à borda do rodapé, como o bloco legal do outro lado. */}
            <button
              type="button"
              onClick={scrollToTop}
              className="btn-ghost btn-sm -mr-3 self-end text-sm text-white/80 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-dark md:self-start"
            >
              Voltar ao topo
              <Icon name="arrow_upward" className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
